import { useReduxState } from 'hooks';
import { LinkButton, MoreOptions } from 'components/common';
import { UsersSendMessage } from 'components/users';
import {
  AchievementGroupOfUsersAddMembers,
  AchievementGroupOfUsersArchive,
  AchievementGroupOfUsersRemove,
  AchievementGroupOfUsersRemoveMembers,
  AchievementGroupOfUsersUpdate
} from '.';

import type { GetAchievementGroupCompletionsRes } from 'shared-types/responses';

interface Props {
  groupCompletion: GetAchievementGroupCompletionsRes['groupCompletions'][number];
  id: string;
  isInDetailsPage?: boolean;
  isSupervisorPage?: boolean;
  updateGroups: () => Promise<void>;
}

const AchievementGroupsOfUsersListActions = ({
  groupCompletion,
  id,
  isInDetailsPage = false,
  isSupervisorPage = false,
  updateGroups
}: Props) => {
  const {
    messages,
    pageState: { achievement },
    serverConfig: { adminUrl },
    user
  } = useReduxState();

  const doNotShowActions =
    !achievement.isOwnerOrSuperadmin &&
    !(achievement.isSupervisor && isSupervisorPage) &&
    !achievement.userAdminPermissions.includes('manage-users');

  return (
    <div
      style={{
        alignItems: 'center',
        display: 'flex',
        gap: 10,
        justifyContent: 'flex-end'
      }}
    >
      {!isInDetailsPage ? (
        <LinkButton
          category="lighter"
          href={`/admin/achievements/${achievement.id}/group-conversations/${groupCompletion.achievementData.id}`}
          icon="comments"
          size="header"
          tooltip={messages.achievementsMarkOrUpdateAnswers}
        />
      ) : null}

      {doNotShowActions ? null : (
        <MoreOptions
          renderInPortal={true}
          styleContentButtons={false}
          {...(!isInDetailsPage ? { category: 'lighter' } : {})}
        >
          <UsersSendMessage
            buttonIsDropdown={true}
            emailButtonTarget={`${adminUrl}/achievements/${achievement.id}`}
            id={`${id}-send-message`}
            userSelectionParams={{
              selectedUserIds: groupCompletion.members
                .filter(({ id, email }) => email && id !== user?.id)
                .map((member) => member.id),
              userSelector: 'emails'
            }}
          />

          <AchievementGroupOfUsersUpdate
            groupCompletion={groupCompletion}
            updateGroups={updateGroups}
          />

          {groupCompletion.achievementData.isConfidential ||
          groupCompletion.achievementData.isClosed ||
          groupCompletion.achievementData.isInactive ? null : (
            <>
              <AchievementGroupOfUsersAddMembers
                groupCompletion={groupCompletion}
                updateGroups={updateGroups}
              />

              <AchievementGroupOfUsersRemoveMembers
                groupCompletion={groupCompletion}
                updateGroups={updateGroups}
              />
            </>
          )}

          {groupCompletion.achievementData.status !== 'completed' ||
          groupCompletion.achievementData.isInactive ? null : (
            <AchievementGroupOfUsersArchive
              groupCompletion={groupCompletion}
              updateGroups={updateGroups}
            />
          )}

          {!isInDetailsPage ? (
            <AchievementGroupOfUsersRemove
              groupCompletion={groupCompletion}
              updateGroups={updateGroups}
            />
          ) : null}
        </MoreOptions>
      )}
    </div>
  );
};

export default AchievementGroupsOfUsersListActions;
